/* eslint-disable react-refresh/only-export-components */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CampaignInfoPopover } from './components/CampaignInfoPopover';
import { CampaignPlayButton } from './components/CampaignPlayButton';
import { CampaignRowActions } from './components/CampaignRowActions';
import { CampaignStatusBadge } from './components/CampaignStatusBadge';
import { SkippedNumbersButton } from './components/SkippedNumbersDialog';
import { StatusLogsButton } from './components/StatusLogsDialog';
import {
  pendingCalls,
  statusLabel,
  templateLabel,
  totalCallsDialed,
  type CampaignRow,
  type ReportDownloadKind,
} from './types';

/**
 * The legacy `ListVoiceCampaign` renders a tiny `fa-download` glyph next to
 * each counter cell so the user can fetch that specific report inline. The
 * icon is gated on the campaign's per-report status flags:
 *
 *   DTMF / DTMF1 / DTMF2 → `dtmfReportStatus === '2'` (or undefined)
 *   Connected Calls       → `ccReportStatus === '2'` (or undefined) AND
 *                           `callsConnected > 0`
 *   SMS                   → `smsStatus === '2'` (or undefined) AND `smsCount > 0`
 *
 * Plus template-id gating (DTMF columns only render for templates 1/2/3
 * etc) — encoded in the helper below.
 */
function canDownload(row: CampaignRow, kind: ReportDownloadKind): boolean {
  const tpl = row.templateId;
  switch (kind) {
    case 'dtmf':
      return (
        (tpl === 1 || tpl === 2 || tpl === 3) &&
        (row.dtmfReportStatus === '2' || row.dtmfReportStatus === undefined)
      );
    case 'dtmf1':
    case 'dtmf2':
      return tpl === 3 && (row.dtmfReportStatus === '2' || row.dtmfReportStatus === undefined);
    case 'connected_calls':
      return (
        Number(row.callsConnected ?? 0) > 0 &&
        (row.ccReportStatus === '2' || row.ccReportStatus === undefined)
      );
    case 'sms':
      return (
        Number(row.smsCount ?? 0) > 0 &&
        (String(row.smsStatus) === '2' || row.smsStatus === undefined)
      );
    case 'pending':
      return true;
  }
}

/** Inline cell wrapper: count + (conditionally) download icon. */
function CountWithDownload ({
  count,
  show,
  onClick,
  busy,
}: {
  count: number | string;
  show: boolean;
  onClick?: () => void;
  busy?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <span>{count}</span>
      {show && onClick && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('h-5 w-5 text-muted-foreground hover:text-foreground')}
          onClick={onClick}
          disabled={busy}
          aria-label="Download report"
          title="Download report"
        >
          <Download className="h-3 w-3" />
        </Button>
      )}
    </span>
  );
}

/**
 * Cell wrapper that stacks the count + "(X%)" suffix on top and a thin
 * colored progress bar underneath. Used for Connected / Pending / DND /
 * Retry Count cells so the user can see each row's share of
 * `numbersUploaded` at a glance.
 *
 * `tone` controls the bar color; `pctNum` is the raw percentage (0-100)
 * used for the bar width. If an `inner` node is provided (e.g. the
 * download-icon CountWithDownload), we render that as the top line instead
 * of a plain count.
 */
type BarTone = 'green' | 'yellow' | 'red' | 'purple' | 'blue';

const BAR_CLASSES: Record<BarTone, string> = {
  green: 'bg-emerald-500',
  yellow: 'bg-amber-500',
  red: 'bg-rose-500',
  purple: 'bg-violet-500',
  blue: 'bg-sky-500',
};

function CountWithPercent({
  count,
  pct,
  pctNum,
  tone,
  inner,
}: {
  count: number | string;
  pct: string;
  pctNum: number;
  tone: BarTone;
  inner?: React.ReactNode;
}) {
  const width = Math.max(0, Math.min(100, pctNum));
  return (
    <div className="flex w-full flex-col gap-1 leading-tight">
      <div className="inline-flex items-baseline gap-1 tabular-nums">
        {inner ?? <span>{count}</span>}
        {pct && <span className="text-[11px] text-muted-foreground">({pct})</span>}
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full rounded-full transition-all', BAR_CLASSES[tone])}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

const num = (v: number | undefined | null): string =>
  v === null || v === undefined ? '—' : v.toLocaleString();

const text = (v: string | number | undefined | null): string => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

/**
 * The full set of columns. Pages pick which to show by passing an
 * `initialColumnVisibility` map — users can still toggle the hidden ones
 * back on via the Columns dropdown.
 */
export function buildCampaignColumns(opts?: {
  /** Adds User ID / Owner columns at the start. */
  showOwner?: boolean;
  /** SADMIN-only: show the reseller's username for each user-owned campaign. */
  showParentUsername?: boolean;
  /** Hide row actions (e.g. for read-only historical/admin views). */
  hideActions?: boolean;
  /** Override the action menu — defaults to the generic CampaignRowActions. */
  renderActions?: (row: CampaignRow) => React.ReactNode;
  /**
   * SADMIN-only: when set, renders a 👁 button next to `numbersUploaded`
   * for non-continuous campaigns that fetches and opens the base file URL.
   */
  onViewBase?: (row: CampaignRow) => void;
  /**
   * When set, renders an inline ⬇ button next to the DTMF / DTMF1 / DTMF2 /
   * Connected / Pending / SMS counter cells. The callback fires
   * `POST /api/obd/report/generate` with the appropriate `reportType`.
   */
  onDownloadReport?: (row: CampaignRow, kind: ReportDownloadKind) => void;
  /**
   * Aggregate `numbersUploaded` across the visible rows. Used to render a
   * "(X%)" suffix beneath the Connected / Pending / DND / Retry-Count cells
   * so the user can eyeball each row's contribution to the totals.
   * 0 / undefined → no percentages shown.
   */
  baseTotal?: number;
}): ColumnDef<CampaignRow>[] {
  const {
    showOwner = false,
    showParentUsername = false,
    hideActions = false,
    renderActions,
    onViewBase,
    onDownloadReport,
    baseTotal = 0,
  } = opts ?? {};

  /**
   * Compute the per-row percentage of `numbersUploaded` (legacy behavior)
   * once and return both the formatted "X%" label and the raw number for
   * the progress-bar width.
   */
  const rowPct = (
    row: CampaignRow,
    value: number | undefined | null,
  ): { label: string; num: number } => {
    const uploaded = Number(row.numbersUploaded ?? 0);
    if (!uploaded || !value) return { label: '', num: 0 };
    const num = (Number(value) / uploaded) * 100;
    return { label: `${Math.round(num)}%`, num };
  };

  // Avoid unused-warning when baseTotal is left at 0 (default-hidden pages).
  void baseTotal;

  const cols: ColumnDef<CampaignRow>[] = [
    {
      accessorKey: 'campaignId',
      header: 'Campaign ID',
      size: 130,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1.5">
          <CampaignPlayButton campaignId={row.original.campaignId} />
          <span className="font-mono text-xs text-muted-foreground">{row.original.campaignId}</span>
        </span>
      ),
    },
    {
      accessorKey: 'campaignName',
      header: 'Campaign',
      cell: ({ row }) => {
        const r = row.original;
        return (
          <Link
            to={`/voice-campaign-info/${r.campaignId}`}
            className="font-medium text-foreground hover:underline"
          >
            {r.campaignName}
          </Link>
        );
      },
    },
  ];

  if (showOwner) {
    cols.push(
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Owner',
        size: 160,
        cell: ({ row }) => text(row.original.username),
      },
    );
  }

  if (showParentUsername) {
    cols.push(
      {
        accessorKey: 'parentUsername',
        header: 'Parent 1',
        size: 140,
        cell: ({ row }) => text(row.original.parentUsername),
      },
      {
        accessorKey: 'grandparentUsername',
        header: 'Parent 2',
        size: 140,
        cell: ({ row }) => text(row.original.grandparentUsername),
      },
      {
        accessorKey: 'accountType',
        header: 'Account Type',
        size: 140,
        meta: {
          exportValue: (row: CampaignRow) =>
            row.accountType === 1
              ? 'Transactional'
              : row.accountType === 3
                ? 'Both'
                : 'Promotional',
        },
        cell: ({ row }) => {
          const t = row.original.accountType;
          const label = t === 1 ? 'Transactional' : t === 3 ? 'Both' : 'Promotional';
          const color =
            t === 1
              ? 'border-blue-200 bg-blue-50 text-blue-900'
              : t === 3
                ? 'border-purple-200 bg-purple-50 text-purple-900'
                : 'border-amber-200 bg-amber-50 text-amber-900';
          return (
            <Badge variant="outline" className={cn('font-normal', color)}>
              {label}
            </Badge>
          );
        },
      },
    );
  }

  cols.push(
    {
      accessorKey: 'campaignStatus',
      header: 'Status',
      size: 140,
      meta: {
        exportValue: (row: CampaignRow) => statusLabel(row.campaignStatus),
      },
      cell: ({ row }) => <CampaignStatusBadge status={row.original.campaignStatus} />,
    },
    {
      accessorKey: 'templateId',
      header: 'Campaign Type',
      size: 150,
      meta: {
        exportValue: (row: CampaignRow) => templateLabel(row.templateId),
      },
      cell: ({ row }) => {
        const r = row.original;
        const isApi = (r.reqSrc ?? 0) === 1;
        return (
          <span className="inline-flex items-center gap-1.5">
            {templateLabel(r.templateId)}
            <span
              className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none ${isApi ? 'bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'}`}
            >
              {isApi ? 'API' : 'Panel'}
            </span>
          </span>
        );
      },
    },
    /*
     * Legacy renames this header to "Creation Time" for continuous (template 10)
     * and "Start Time" otherwise. We expose the same — checked per row since
     * a single list can mix continuous + standard campaigns.
     */
    // User-entered schedule — populated only for send-later campaigns.
    {
      id: 'scheduleTime',
      accessorFn: (r) => r.scheduledAt ?? '',
      header: 'Schedule Time',
      size: 180,
      cell: ({ row }) => text(row.original.scheduledAt),
    },
    // Actual start of the campaign (hidden/shown independently of Schedule Time).
    {
      id: 'startTime',
      accessorFn: (r) => r.scheduleTime ?? '',
      header: 'Start Time',
      size: 180,
      cell: ({ row }) => text(row.original.scheduleTime),
    },
    {
      accessorKey: 'endTime',
      header: 'End Time',
      size: 180,
      cell: ({ row }) => text(row.original.endTime),
    },
    {
      accessorKey: 'pauseTime',
      header: 'Pause Time',
      size: 200,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1">
          {text(row.original.pauseTime)}
          {row.original.pauseTime && row.original.pauseTime !== '—' && (
            <StatusLogsButton campaignId={row.original.campaignId} />
          )}
        </span>
      ),
    },
    {
      accessorKey: 'resumeTime',
      header: 'Resume Time',
      size: 200,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1">
          {text(row.original.resumeTime)}
          {row.original.resumeTime && row.original.resumeTime !== '—' && (
            <StatusLogsButton campaignId={row.original.campaignId} />
          )}
        </span>
      ),
    },
    /*
     * Continuous-only columns. Legacy gated them on a list-wide
     * `continuousFlag`; we mark them default-hidden but users can toggle
     * back on from the Columns dropdown.
     */

    {
      accessorKey: 'flowEndTime',
      header: 'End Date',
      size: 160,
      cell: ({ row }) => text(row.original.flowEndTime),
    },
    {
      accessorKey: 'numbersUploaded',
      header: 'Uploaded',
      size: 110,
      cell: ({ row }) => {
        const r = row.original;
        const showEye = onViewBase && (r.continuous ?? 0) === 0;
        return (
          <span className="inline-flex items-center gap-1">
            <span>{num(r.numbersUploaded)}</span>
            {showEye && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => onViewBase(r)}
                aria-label="View base file"
                title="View base file"
              >
                <Eye className="h-3.5 w-3.5" />
              </Button>
            )}
          </span>
        );
      },
    },
    {
      id: 'skippedCount',
      header: 'Skipped',
      size: 90,
      cell: ({ row }) => {
        const skipped = row.original.skippedCount ?? 0;
        return (
          <span className="inline-flex items-center gap-1">
            <span className={skipped > 0 ? 'text-amber-600' : 'text-muted-foreground'}>
              {num(skipped)}
            </span>
            {skipped > 0 && (row.original.continuous ?? 0) === 0 && (
              <SkippedNumbersButton campaignId={row.original.campaignId} count={skipped} />
            )}
          </span>
        );
      },
    },
    /*
     * TCD = Total Calls Dialed (numbersProcessed + retryCount). Legacy hides
     * this for ROLE_USER unless `hasTcdCount` is set; default-visible
     * everywhere else.
     */
    {
      id: 'totalCallsDialed',
      header: 'TCD',
      size: 110,
      accessorFn: totalCallsDialed,
      cell: ({ row }) => num(totalCallsDialed(row.original)),
    },
    {
      accessorKey: 'numbersProcessed',
      header: 'Dialed',
      size: 110,
      cell: ({ row }) => num(row.original.numbersProcessed),
    },
    {
      id: 'pending',
      header: 'Pending',
      size: 150,
      accessorFn: pendingCalls,
      cell: ({ row }) => {
        const value = pendingCalls(row.original);
        const p = rowPct(row.original, value);
        return (
          <CountWithPercent
            count={num(value)}
            pct={p.label}
            pctNum={p.num}
            tone="yellow"
            inner={
              <CountWithDownload
                count={num(value)}
                show={Boolean(onDownloadReport) && canDownload(row.original, 'pending')}
                onClick={() => onDownloadReport?.(row.original, 'pending')}
              />
            }
          />
        );
      },
    },
    {
      accessorKey: 'callsConnected',
      header: 'Connected',
      size: 150,
      cell: ({ row }) => {
        const p = rowPct(row.original, row.original.callsConnected);
        return (
          <CountWithPercent
            count={num(row.original.callsConnected)}
            pct={p.label}
            pctNum={p.num}
            tone="green"
            inner={
              <CountWithDownload
                count={num(row.original.callsConnected)}
                show={Boolean(onDownloadReport) && canDownload(row.original, 'connected_calls')}
                onClick={() => onDownloadReport?.(row.original, 'connected_calls')}
              />
            }
          />
        );
      },
    },
    {
      accessorKey: 'pulses',
      header: 'Pulses',
      size: 110,
      cell: ({ row }) => num(row.original.pulses),
    },
    {
      accessorKey: 'dndCount',
      header: 'DND',
      size: 140,
      cell: ({ row }) => {
        const p = rowPct(row.original, row.original.dndCount);
        return (
          <CountWithPercent
            count={num(row.original.dndCount)}
            pct={p.label}
            pctNum={p.num}
            tone="red"
          />
        );
      },
    },
    {
      accessorKey: 'dtmfCount',
      header: 'DTMF',
      size: 110,
      cell: ({ row }) => (
        <CountWithDownload
          count={num(row.original.dtmfCount)}
          show={Boolean(onDownloadReport) && canDownload(row.original, 'dtmf')}
          onClick={() => onDownloadReport?.(row.original, 'dtmf')}
        />
      ),
    },
    {
      accessorKey: 'dtmf1Count',
      header: 'DTMF1',
      size: 110,
      cell: ({ row }) => (
        <CountWithDownload
          count={num(row.original.dtmf1Count)}
          show={Boolean(onDownloadReport) && canDownload(row.original, 'dtmf1')}
          onClick={() => onDownloadReport?.(row.original, 'dtmf1')}
        />
      ),
    },
    {
      accessorKey: 'dtmf2Count',
      header: 'DTMF2',
      size: 110,
      cell: ({ row }) => (
        <CountWithDownload
          count={num(row.original.dtmf2Count)}
          show={Boolean(onDownloadReport) && canDownload(row.original, 'dtmf2')}
          onClick={() => onDownloadReport?.(row.original, 'dtmf2')}
        />
      ),
    },
    {
      accessorKey: 'smsCount',
      header: 'SMS',
      size: 110,
      cell: ({ row }) => (
        <CountWithDownload
          count={num(row.original.smsCount)}
          show={Boolean(onDownloadReport) && canDownload(row.original, 'sms')}
          onClick={() => onDownloadReport?.(row.original, 'sms')}
        />
      ),
    },
    {
      accessorKey: 'ttsCount',
      header: 'TTS',
      size: 100,
      cell: ({ row }) => num(row.original.ttsCount),
    },
    {
      accessorKey: 'retryCount',
      header: 'Retry Count',
      size: 150,
      cell: ({ row }) => {
        const p = rowPct(row.original, row.original.retryCount);
        return (
          <CountWithPercent
            count={num(row.original.retryCount)}
            pct={p.label}
            pctNum={p.num}
            tone="purple"
          />
        );
      },
    },
    /*
     * `retries` is the configured number of retry attempts on the campaign
     * (not the actual retry count). Legacy hides this unless any row in the
     * list has retries — we keep it togglable.
     */
    {
      accessorKey: 'retries',
      header: 'No. Of Retries',
      size: 130,
      cell: ({ row }) => num(row.original.retries),
    },
    {
      accessorKey: 'failedCount',
      header: 'Failed Count',
      size: 130,
      cell: ({ row }) => num(row.original.failedCount),
    },
    {
      accessorKey: 'webhookFailedCount',
      header: 'Webhook Failed',
      size: 140,
      cell: ({ row }) => num(row.original.webhookFailedCount),
    },
    {
      accessorKey: 'webhookEvents',
      header: 'Webhook/SMS Events',
      size: 160,
      cell: ({ row }) => {
        const events = row.original.webhookEvents;
        if (!events) return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {events.split(',').map((e) => (
              <Badge key={e} variant="outline" className="text-2xs font-normal">
                {e.trim()}
              </Badge>
            ))}
          </div>
        );
      },
    },
    /*
     * The list endpoint doesn't carry location / CLI data per row — both
     * are click-to-load via `GET /api/obd/campaign/info/{id}`. Render a
     * Location / CLI button that opens a popover and fetches on demand.
     * Mirrors legacy ListVoiceCampaign's "Location" / "CLI" dropdowns.
     */
    {
      id: 'locationList',
      header: 'Location',
      size: 130,
      enableSorting: false,
      cell: ({ row }) => (
        <CampaignInfoPopover campaignId={row.original.campaignId} kind="location" />
      ),
    },
    {
      id: 'cliList',
      header: 'CLI',
      size: 120,
      enableSorting: false,
      cell: ({ row }) => <CampaignInfoPopover campaignId={row.original.campaignId} kind="cli" />,
    },
    /*
     * TTS Flow Id — continuous-only column. Legacy renders the id as a link
     * that opens `/view-tts-flow/{id}`.
     */
    {
      accessorKey: 'ttsFlowId',
      header: 'TTS Flow Id',
      size: 130,
      cell: ({ row }) => {
        const id = row.original.ttsFlowId;
        if (id === undefined || id === null || id === '') return '—';
        return (
          <Link
            to={`/view-tts-flow/${id}`}
            className="font-mono text-xs text-foreground hover:underline"
          >
            {id}
          </Link>
        );
      },
    },
  );

  if (!hideActions) {
    cols.push({
      id: 'actions',
      header: 'Settings',
      size: 90,
      enableHiding: false,
      enableSorting: false,
      cell: ({ row }) =>
        renderActions
          ? renderActions(row.original)
          : <CampaignRowActions campaign={row.original} />,
    });
  }

  return cols;
}

/**
 * The "always visible" column set. Anything not in this map starts hidden
 * but the user can re-enable via the Columns toggle. Pages may override per
 * use-case (e.g. Schedule hides "Connected" because it's always 0).
 */
/**
 * Mirrors legacy `ListVoiceCampaign` default visibility. The DTMF / SMS /
 * TTS counters are all shown by default — only `retryCount` and
 * `failedCount` start hidden because legacy gates them on
 * `hasRetries` / `continuousFlag` (we don't pre-fetch either flag, so
 * keep them togglable from the Columns dropdown).
 */
export const DEFAULT_VISIBLE_COLUMNS: Record<string, boolean> = {
  campaignId: true,
  campaignName: true,
  campaignStatus: true,
  templateId: true,
  scheduleTime: true,
  startTime: true,
  endTime: true,
  pauseTime: true,
  resumeTime: true,
  numbersUploaded: true,
  skippedCount: true,
  numbersProcessed: true,
  pending: true,
  callsConnected: true,
  pulses: true,
  dndCount: true,
  dtmfCount: true,
  dtmf1Count: true,
  dtmf2Count: true,
  smsCount: true,
  ttsCount: true,
  webhookFailedCount: true,
  locationList: true,
  cliList: true,
  // Legacy: shown only when the list is continuous (template 10).
  // We don't pre-compute that across the dataset — default-hide.
  flowEndTime: false,
  ttsFlowId: false,
  // Legacy: hidden unless the list has retries — same default-hide approach.
  retryCount: false,
  retries: false,
  failedCount: false,
  // Legacy: hidden for ROLE_USER without hasTcdCount.
  totalCallsDialed: false,
};